import React from "react";
import { cn } from "../utils/cn";
import { MetricDelta } from "./MetricDelta";

type StatCardColor = "green" | "blue" | "purple" | "teal";

type MetricType = "currency" | "percentage" | "score";

interface DeltaInfo {
  delta: number;
  type: MetricType;
  stagger?: number;
  previousValue?: number;
  currentValue?: number;
}

const colorToCardClass: Record<StatCardColor, string> = {
  green: "stat-card--green",
  blue: "stat-card--blue",
  purple: "stat-card--purple",
  teal: "stat-card--teal",
};

const colorToIconClass: Record<StatCardColor, string> = {
  green: "stat-card-icon--green",
  blue: "stat-card-icon--blue",
  purple: "stat-card-icon--purple",
  teal: "stat-card-icon--teal",
};

const colorToValueClass: Record<StatCardColor, string> = {
  green: "stat-card-value--positive",
  blue: "stat-card-value--neutral",
  purple: "stat-card-value--neutral",
  teal: "stat-card-value--neutral",
};

/** Values that indicate an empty / zero / missing data state */
const EMPTY_VALUE_PATTERNS = new Set(["$0", "---", "0%"]);

interface StatCardProps {
  label: string;
  value: string;
  subtitle?: string;
  color?: StatCardColor;
  /** Emoji or text icon rendered in a colored circle */
  icon?: string;
  /** Optional delta info to show inline metric change badge */
  deltaInfo?: DeltaInfo;
}

export const StatCard = React.memo(
  ({
    label,
    value,
    subtitle,
    color = "blue",
    icon,
    deltaInfo,
  }: StatCardProps) => {
    return (
      <div
        className={cn("stat-card", colorToCardClass[color])}
        id={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {icon && (
          <div className={cn("stat-card-icon", colorToIconClass[color])}>
            {icon}
          </div>
        )}
        <div className="stat-card-body">
          <p className="stat-card-label">{label}</p>
          <p
            className={cn(
              "stat-card-value",
              colorToValueClass[color],
              EMPTY_VALUE_PATTERNS.has(value) && "stat-card-value--empty"
            )}
            {...(EMPTY_VALUE_PATTERNS.has(value) && {
              "aria-label": `${label}: no data`,
            })}
          >
            {value}
            {deltaInfo && deltaInfo.delta !== 0 && (
              <MetricDelta
                delta={deltaInfo.delta}
                type={deltaInfo.type}
                stagger={deltaInfo.stagger}
                previousValue={deltaInfo.previousValue}
                currentValue={deltaInfo.currentValue}
              />
            )}
          </p>
          {subtitle && <p className="stat-card-subtitle">{subtitle}</p>}
        </div>
      </div>
    );
  }
);

StatCard.displayName = "StatCard";
